
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSidebarLinks, SidebarLink } from "@/hooks/useSidebarLinks";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useModalStore } from "@/services/modalStore";

interface SidebarContentProps {
  onClose: () => void;
}

const SidebarContent = ({ onClose }: SidebarContentProps) => {
  const navigate = useNavigate();
  const { links, pendingItemsCount, hasSharedLists, primaryList } = useSidebarLinks();
  const { theme } = useThemeConfig();
  const modalStore = useModalStore();

  const handleLinkClick = (link: SidebarLink) => {
    if (modalStore.activeModal) {
      modalStore.closeModal();
    }
    if (link.fetchPrimary && primaryList) {
      navigate(`/shopping?list=${primaryList.id}`);
    } else {
      navigate(link.href);
    }
    onClose();
  };

  return (
    <div
      className="flex flex-col gap-y-3 overflow-y-auto overflow-x-hidden border-r h-full relative"
      style={{
        backgroundColor: theme.sidebarColor,
        color: theme.textColor,
        borderColor: theme.borderColor,
      }}
    >
      <nav className="flex flex-col flex-1 px-2 pt-4">
        {links.map((section) => (
          <div key={section.section} className="mb-4">
            <h2
              className="text-xs font-semibold mb-2 px-3"
              style={{ color: theme.textPrimaryColor }}
            >
              {section.section}
            </h2>
            <ul className="space-y-1">
              {section.items.map((link) => (
                <li key={link.name}>
                  <NavLink
                    to={link.href}
                    className={(
                      { isActive }) =>
                        `flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                          isActive ? 'font-semibold' : ''
                        }`
                    }
                    style={({ isActive }) => ({
                      backgroundColor: isActive ? theme.primaryColor : undefined,
                      color: isActive ? theme.textPrimaryColor : theme.textPrimaryColor,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClick(link);
                    }}
                  >
                    <link.icon className="h-4 w-4" />
                    {link.name}
                    
                    {/* Shopping pending items badge */}
                    {link.fetchPending && pendingItemsCount > 0 && (
                      <span
                        className="ml-auto text-xs font-semibold rounded-full flex items-center justify-center min-w-5 h-5 px-1"
                        style={{
                          backgroundColor: theme.primaryColor,
                          color: theme.backgroundColor,
                        }}
                      >
                        {pendingItemsCount > 99 ? '99+' : pendingItemsCount}
                      </span>
                    )}
                    
                    {/* Shared lists notification badge */}
                    {link.fetchShared && hasSharedLists && (
                      <span
                        className="ml-auto text-xs font-semibold rounded-full flex items-center justify-center min-w-5 h-5 px-1"
                        style={{
                          backgroundColor: theme.warningColor,
                          color: theme.textPrimaryColor,
                        }}
                      >
                        !
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );
};

export default SidebarContent;
